import React from 'react';
import { Badge, Box, ComboboxItem, ComboboxLikeRenderOptionInput, Flex, Group, Loader, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { IOTP } from '../../../global/entities/OTP/OTP';
import { useOTPsByPatientId } from '../../../global/entities/OTP/queries';
import { PatientStore } from '../../../global/Store/PatientStore';
import { OTPEventTimeline } from './OTPEventTimeline';
import TaperingStepsTable from './TaperingStepsTable';
import {
    convertTaperingStepsToChartData,
    computeVirtualMMEArray,
    formatDateTick,
    getStatusColor,
    useGetStatusLabel,
    CustomActualMMEDot,
    CustomActualMMEActiveDot,
    CustomTooltip,
} from './OTPChartHelpers';
import { OTPParametersSummaryBar } from './OTPParametersSummaryBar';

interface IOTPHistorySectionProps {
    patientStore?: PatientStore;
}

const OTPHistorySectionComponent: React.FC<IOTPHistorySectionProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t, i18n } = useTranslation();
        const getStatusLabel = useGetStatusLabel();
        const patientId = patientStore?.patientId ? Number(patientStore.patientId) : undefined;
        const { data: plans, isLoading } = useOTPsByPatientId(patientId);
        const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(null);

        const selectedPlan: IOTP | undefined = React.useMemo(() => {
            if (!selectedPlanId || !plans) return undefined;
            return plans.find((p) => p.id === Number(selectedPlanId));
        }, [selectedPlanId, plans]);

        const virtualMMEArray = React.useMemo(() => {
            if (!selectedPlan?.taperingSteps?.length) return undefined;
            return computeVirtualMMEArray(selectedPlan.taperingSteps);
        }, [selectedPlan]);

        const chartData = React.useMemo(() => {
            if (!selectedPlan || !selectedPlan.taperingSteps?.length) return [];
            return convertTaperingStepsToChartData(selectedPlan.taperingSteps, i18n.language, undefined, virtualMMEArray);
        }, [selectedPlan, i18n.language, virtualMMEArray]);

        const hasActualMME = chartData.some((d) => d.actualMME !== undefined);

        const formatPlanDate = (dateStr: string): string => {
            const localeTag = i18n.language === 'fr' ? 'fr-CA' : 'en-CA';
            return new Date(dateStr).toLocaleDateString(localeTag, { year: 'numeric', month: 'short', day: 'numeric' });
        };

        // Map planId → status for use in renderOption
        const planStatusMap = React.useMemo(() => {
            if (!plans) return new Map<string, number>();
            return new Map(plans.map((p) => [String(p.id), p.status]));
        }, [plans]);

        const selectOptions = React.useMemo(() => {
            if (!plans) return [];
            return plans.map((plan) => ({
                value: String(plan.id),
                label: plan.name || `#${plan.id}`,
            }));
        }, [plans]);

        const maxLabelWidth = React.useMemo(() => {
            const maxLen = selectOptions.reduce((max, opt) => Math.max(max, opt.label.length), 0);
            return `${Math.max(maxLen * 0.6, 5)}em`;
        }, [selectOptions]);

        const renderPlanOption = React.useCallback(
            ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
                const status = planStatusMap.get(option.value);
                return (
                    <Group gap="sm" wrap="nowrap">
                        <Text size="sm" style={{ minWidth: maxLabelWidth, display: 'inline-block' }}>{option.label}</Text>
                        {status !== undefined && (
                            <Badge size="sm" color={getStatusColor(status)} variant="filled">
                                {getStatusLabel(status)}
                            </Badge>
                        )}
                    </Group>
                );
            },
            [planStatusMap, getStatusLabel, maxLabelWidth],
        );

        if (isLoading) {
            return (
                <Flex justify="center" align="center" py="xl">
                    <Loader size="md" />
                </Flex>
            );
        }

        if (!plans || plans.length === 0) {
            return (
                <Paper p="xl" withBorder>
                    <Text ta="center" c="dimmed">
                        {t('otp.history.noPlans', 'No tapering plans found for this patient')}
                    </Text>
                </Paper>
            );
        }

        return (
            <Stack gap="md">
                {/* Plan selector */}
                <Select
                    label={t('otp.history.selectPlan', 'Select a plan')}
                    placeholder={t('otp.history.selectPlan', 'Select a plan')}
                    data={selectOptions}
                    value={selectedPlanId}
                    onChange={setSelectedPlanId}
                    renderOption={renderPlanOption}
                    searchable
                    clearable
                    style={{ maxWidth: '50%' }}
                />

                {!selectedPlan && (
                    <Paper p="xl" withBorder>
                        <Text ta="center" c="dimmed">
                            {t('otp.history.noPlan', 'No plan selected')}
                        </Text>
                    </Paper>
                )}

                {selectedPlan && (
                    <Stack gap="md">
                        {/* Status bar */}
                        <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                            <Flex p="md" justify="space-between" align="center" style={{ backgroundColor: '#f9f9f9', borderRadius: '4px 4px 0 0' }}>
                                <Flex gap="sm" align="center" wrap="wrap" style={{ flex: 1 }}>
                                    <Title order={5}>{t('otp.status.title', 'Status')}</Title>
                                    <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
                                    <Badge size="lg" color={getStatusColor(selectedPlan.status)} variant="filled">
                                        {getStatusLabel(selectedPlan.status)}
                                    </Badge>
                                    {selectedPlan.name && (
                                        <Text size="sm" fw={500}>{selectedPlan.name}</Text>
                                    )}
                                    <Text size="sm" c="dimmed">
                                        {selectedPlan.originalMME} {'→'} {selectedPlan.parameters?.targetFinalMME ?? 0} MME
                                    </Text>
                                </Flex>
                                <Group gap="sm">
                                    <Text size="sm" c="dimmed">
                                        {t('otp.history.createdOn', { date: formatPlanDate(selectedPlan.createdDate), defaultValue: `Created on ${formatPlanDate(selectedPlan.createdDate)}` })}
                                    </Text>
                                </Group>
                            </Flex>
                            <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                                {selectedPlan.delegatedToHcpName && (
                                    <Text size="sm" fw={500} mb="sm">
                                        {t('otp.planManager.delegatedTo', 'Delegated to')}: {selectedPlan.delegatedToHcpName}
                                    </Text>
                                )}
                                <Text size="sm" c="dimmed" mb="xs">
                                    {t('otp.planManager.createdBy', 'Created by')}: {selectedPlan.createdByHcpName}
                                </Text>
                                <OTPEventTimeline otpId={selectedPlan.id} />
                            </Box>
                        </Box>

                        {/* Parameters display (read-only) */}
                        <OTPParametersSummaryBar
                            originalMME={selectedPlan.originalMME}
                            targetFinalMME={selectedPlan.parameters?.targetFinalMME ?? 0}
                            reductionPercentage={selectedPlan.parameters.reductionPercentage}
                            daysPerStep={selectedPlan.parameters.daysPerStep}
                            totalDurationDays={selectedPlan.totalDurationDays}
                        />

                        {/* Tapering chart */}
                        {chartData.length > 0 && (
                            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                                <Flex p="md" style={{ backgroundColor: '#f9f9f9', borderRadius: '4px 4px 0 0' }}>
                                    <Title order={5}>{t('otp.graph.title', 'Tapering Graph')}</Title>
                                </Flex>
                                <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                                    <Box style={{ height: 350 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                                <XAxis
                                                    dataKey="date"
                                                    type="number"
                                                    scale="time"
                                                    domain={['dataMin', 'dataMax']}
                                                    tickFormatter={(ts) => formatDateTick(ts, i18n.language)}
                                                    label={{ value: t('otp.graph.xaxis', 'Date'), position: 'insideBottom', offset: -10 }}
                                                    stroke="#666"
                                                />
                                                <YAxis
                                                    label={{ value: t('otp.graph.yaxis', 'MME Dose'), angle: -90, position: 'insideLeft' }}
                                                    stroke="#666"
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend verticalAlign="top" height={36} />
                                                
                                                <Line
                                                    type="monotone"
                                                    dataKey="upperBound"
                                                    stroke="#90caf9"
                                                    strokeWidth={1}
                                                    strokeDasharray="5 5"
                                                    dot={false}
                                                    activeDot={false}
                                                    name={t('otp.graph.legend.upperRange', 'Upper Range')}
                                                    legendType="none"
                                                />
                                                <Line
                                                    type="linear"
                                                    dataKey="mme"
                                                    stroke="#1C7ED6"
                                                    strokeWidth={3}
                                                    dot={{ fill: '#1C7ED6', r: 4 }}
                                                    activeDot={{ r: 6 }}
                                                    name={t('otp.graph.legend.targetMME', 'Planned MME')}
                                                />

                                                {/* Virtual MME line (dashed, shown when deltas propagate) */}
                                                <Line
                                                        type="linear"
                                                        dataKey="virtualMME"
                                                        stroke="#9c27b0"
                                                        strokeWidth={2}
                                                        strokeDasharray="6 3"
                                                        dot={{ fill: '#9c27b0', r: 4 }}
                                                        activeDot={{ r: 5 }}
                                                        name={t('otp.graph.legend.virtualMME', 'Virtual MME')}
                                                        connectNulls={false}
                                                    />

                                                {hasActualMME && (
                                                    <Line
                                                        type="linear"
                                                        dataKey="actualMME"
                                                        stroke="#2f9e44"
                                                        strokeWidth={2}
                                                        dot={<CustomActualMMEDot />}
                                                        activeDot={<CustomActualMMEActiveDot />}
                                                        name={t('otp.graph.legend.actualMME', 'Actual MME')}
                                                        connectNulls={false}
                                                    />
                                                )}
                                                
                                                <Line
                                                    type="monotone"
                                                    dataKey="lowerBound"
                                                    stroke="#90caf9"
                                                    strokeWidth={1}
                                                    strokeDasharray="5 5"
                                                    dot={false}
                                                    activeDot={false}
                                                    name={t('otp.graph.legend.lowerRange', 'Lower Range')}
                                                    legendType="none"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Steps table (read-only) */}
                        {selectedPlan.taperingSteps?.length > 0 && (
                            <TaperingStepsTable
                                steps={selectedPlan.taperingSteps}
                                originalMME={selectedPlan.originalMME}
                                isCustomized={false}
                                onStepChange={() => {}}
                                onReset={() => {}}
                                disabled={true}
                                forceExpanded={true}
                                virtualMMEArray={virtualMMEArray}
                            />
                        )}
                    </Stack>
                )}
            </Stack>
        );
    }),
);

export const OTPHistorySection = OTPHistorySectionComponent;
